import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

// Use relative URL for API so it works on any domain
const API = "/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (error) {
        // Token might be a shooter token, check stored user data
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        } else {
          localStorage.removeItem("token");
        }
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const shooterLogin = async (shooterId, pin = '') => {
    const res = await axios.post(`${API}/auth/shooter-login`, { 
      shooter_id: shooterId, 
      pin: pin || null
    });
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, phone, password, registerAsShooter = false, shooterIdNumber = '', pin = '') => {
    const res = await axios.post(`${API}/auth/register`, { 
      username: email, // Use email as username
      name, 
      email, 
      phone, 
      password,
      register_as_shooter: registerAsShooter,
      shooter_id_number: shooterIdNumber,
      pin: pin || null
    });
    
    // Check if user is pending approval
    if (res.data.status === "pending") {
      return { pending: true, message: res.data.message, status: res.data.status };
    }
    
    // Shooter accounts are auto-approved
    if (res.data.status === "approved") {
      return { 
        message: res.data.message, 
        status: res.data.status,
        linked: res.data.linked 
      };
    }
    
    // First user gets auto-approved with token
    if (res.data.token) {
      localStorage.setItem("token", res.data.token);
      setUser(res.data.user);
    }
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const isAdmin = () => user?.role === "admin";
  
  const isShooter = () => user?.role === "shooter";

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      shooterLogin,
      register, 
      logout, 
      isAdmin,
      isShooter,
      getAuthHeader,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
